import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link } from 'react-router-dom';
import { useQuote } from '../../context/QuoteContext';
import { addIncomingQuote } from '../../lib/firestore/incomingQuotesService';

export function QuoteDrawer() {
  const { items, remove, updateQuantity, clear, isDrawerOpen, closeDrawer } = useQuote();

  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', note: '',
  });
  const [agreedKVKK, setAgreedKVKK] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  if (!isDrawerOpen) return null;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || items.length === 0 || !recaptchaValue || !agreedKVKK || !agreedTerms) return;
    setSending(true);

    try {
      await addIncomingQuote({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        note: form.note,
        items: items.map(it => ({
          id: it.id,
          title: it.title,
          quantity: it.quantity,
          unit: it.unit,
          brand: it.brand,
          sku: it.sku,
          image: it.image
        }))
      });

      setSent(true);
    } catch (error) {
      console.error("Error submitting quote request:", error);
      alert("Gönderim sırasında bir hata oluştu, lütfen daha sonra tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    closeDrawer();
    if (sent) {
      setSent(false);
      clear();
    }
  };

  return (
    <>
      <div className="bc-drawer-backdrop" onClick={handleClose} />
      <div className="bc-drawer" role="dialog" aria-modal="true" aria-label="Teklif Sepeti">
        {/* Header */}
        <div className="bc-drawer-header">
          <div>
            <div className="bc-drawer-title">
              {sent ? 'Talebiniz Alındı' : 'Teklif Talebi'}
            </div>
            <div className="bc-drawer-subtitle">
              {sent
                ? 'Talebiniz başarıyla sistemimize iletildi'
                : `${items.length} ürün seçildi`
              }
            </div>
          </div>
          <button className="bc-drawer-close" onClick={handleClose} aria-label="Kapat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="bc-drawer-body">
          {items.length === 0 ? (
            <div className="bc-drawer-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              <p>Sepetiniz boş</p>
              <p style={{ fontSize: '0.78rem', color: '#d1d5db' }}>
                Ürün kartından "Teklif" butonuna tıklayın
              </p>
            </div>
          ) : (
            <>
              {/* Product List */}
              <div className="bc-drawer-products">
                <div className="bc-drawer-products-label">Seçilen Ürünler</div>
                {items.map(item => (
                  <div key={item.id} className="bc-quote-item">
                    <div className="bc-quote-item-img">
                      {item.image
                        ? <img src={item.image} alt={item.title} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                            </svg>
                          </div>
                      }
                    </div>
                    <div className="bc-quote-item-info">
                      <div className="bc-quote-item-title">{item.title}</div>
                      <div className="bc-quote-item-meta">
                        {item.brand}
                        {item.sku && ` · ${item.sku}`}
                        {item.category && ` · ${item.category}`}
                      </div>
                      {/* Miktar — gönderim sonrası salt okunur */}
                      <div className="bc-qty-row">
                        {!sent && (
                          <button
                            className="bc-qty-btn"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            aria-label="Azalt"
                            disabled={item.quantity <= 1}
                          >−</button>
                        )}
                        <input
                          className="bc-qty-input"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={item.quantity === 0 ? '' : item.quantity}
                          readOnly={sent}
                          onChange={sent ? undefined : e => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            if (raw === '') {
                              updateQuantity(item.id, 0);
                            } else {
                              updateQuantity(item.id, parseInt(raw, 10));
                            }
                          }}
                          onBlur={sent ? undefined : () => {
                            if (item.quantity < 1) updateQuantity(item.id, 1);
                          }}
                          aria-label="Miktar"
                          style={sent ? { cursor: 'default', background: 'transparent', border: 'none', fontWeight: 600, color: '#374151', minWidth: 0 } : undefined}
                        />
                        <span className="bc-qty-unit">{item.unit}</span>
                        {!sent && (
                          <button
                            className="bc-qty-btn"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            aria-label="Artır"
                          >+</button>
                        )}
                      </div>
                    </div>
                    {!sent && (
                      <button
                        className="bc-quote-item-remove"
                        onClick={() => remove(item.id)}
                        aria-label="Kaldır"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* ── Başarı mesajı (gönderim sonrası) ── */}
              {sent && (
                <div style={{
                  margin: '1.5rem 0 0',
                  padding: '1.75rem',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%)',
                  border: '1px solid #d1fae5',
                  borderRadius: '16px',
                  textAlign: 'center',
                }}>
                  {/* Onay ikonu */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                    <div style={{
                      width: 56, height: 56,
                      borderRadius: '50%',
                      background: '#22c55e',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(34,197,94,0.25)'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  </div>
                  {/* Mesaj */}
                  <p style={{
                    fontSize: '0.875rem',
                    lineHeight: 1.75,
                    color: '#374151',
                    margin: '0 0 1.5rem',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Teklif talebiniz başarıyla sistemimize ulaşmıştır. Seçmiş olduğunuz ürünler üzerinden projenize en uygun fiyatlandırmayı hazırlayıp, detayları görüşmek üzere en kısa sürede sizinle iletişime geçeceğiz. İlginiz için teşekkür ederiz.
                  </p>
                  {/* Logo */}
                  <div style={{ borderTop: '1px solid #d1fae5', paddingTop: '1.25rem', display: 'flex', justifyContent: 'center' }}>
                    <img
                      src="/yataylogo.svg"
                      alt="Mayi Mimarlık"
                      style={{ height: 36, opacity: 0.85, objectFit: 'contain' }}
                    />
                  </div>
                </div>
              )}

              {/* Form */}
              {!sent && (
                <form className="bc-drawer-form" onSubmit={handleSend} id="quote-form">
                  <div className="bc-form-section-label">İletişim Bilgileri</div>

                  <div className="bc-form-row">
                    <div className="bc-form-field">
                      <label className="bc-form-label" htmlFor="qf-name">
                        Ad Soyad <span>*</span>
                      </label>
                      <input
                        id="qf-name"
                        className="bc-form-input"
                        type="text"
                        placeholder="Ad Soyad"
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="bc-form-field">
                      <label className="bc-form-label" htmlFor="qf-company">Firma</label>
                      <input
                        id="qf-company"
                        className="bc-form-input"
                        type="text"
                        placeholder="Firma Adı"
                        value={form.company}
                        onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="bc-form-row">
                    <div className="bc-form-field">
                      <label className="bc-form-label" htmlFor="qf-email">
                        E-posta <span>*</span>
                      </label>
                      <input
                        id="qf-email"
                        className="bc-form-input"
                        type="email"
                        placeholder="ornek@mail.com"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="bc-form-field">
                      <label className="bc-form-label" htmlFor="qf-phone">
                        Telefon <span>*</span>
                      </label>
                      <input
                        id="qf-phone"
                        className="bc-form-input"
                        type="tel"
                        placeholder="0 (5XX) XXX XX XX"
                        value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="bc-form-field">
                    <label className="bc-form-label" htmlFor="qf-note">Proje Hakkında Detaylı Bilgi (Opsiyonel)</label>
                    <textarea
                      id="qf-note"
                      className="bc-form-textarea"
                      placeholder="Proje alanı, teslimat detayı, miktar veya özel talepleriniz…"
                      value={form.note}
                      onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                    />
                  </div>

                  <div className="bc-form-field pt-2" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', cursor: 'pointer', color: '#4b5563' }}>
                      <input type="checkbox" checked={agreedKVKK} onChange={(e) => setAgreedKVKK(e.target.checked)} required style={{ accentColor: '#c59d3e', width: '16px', height: '16px' }} />
                      <span><Link to="/privacy" target="_blank" style={{ color: '#c59d3e', textDecoration: 'underline' }}>KVKK Aydınlatma Metni</Link>ni okudum, onaylıyorum.</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', cursor: 'pointer', color: '#4b5563' }}>
                      <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} required style={{ accentColor: '#c59d3e', width: '16px', height: '16px' }} />
                      <span><Link to="/terms" target="_blank" style={{ color: '#c59d3e', textDecoration: 'underline' }}>Kullanıcı ve Bayi Satış Sözleşmesi</Link>ni okudum, onaylıyorum.</span>
                    </label>
                    
                    <div style={{ marginTop: '0.5rem' }}>
                      <ReCAPTCHA
                        sitekey="6LfYdKssAAAAAKtxpV5zqmLX9P6NhTorvJRMj5jc"
                        onChange={(val) => setRecaptchaValue(val)}
                      />
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!sent && items.length > 0 && (
          <div className="bc-drawer-footer">
            <button
              className={`bc-drawer-submit${sent ? ' sent' : ''}`}
              type="submit"
              form="quote-form"
              disabled={sending || items.length === 0 || !form.name || !form.email || !form.phone || !recaptchaValue || !agreedKVKK || !agreedTerms}
            >
              {sending ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  Gönderiliyor…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  Teklif Talebi Gönder
                </>
              )}
            </button>
            <p className="bc-drawer-disclaimer">
              Fiyat bilgisi verilmez. Bu form yalnızca ürün bilgisi ve teklif talebine yöneliktir.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Miktar Güncelleme Kontrolü */
        .bc-qty-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
        }
        .bc-qty-btn {
          width: 28px;
          height: 28px;
          border: 1px solid rgba(0,0,0,0.12);
          border-radius: 6px;
          background: rgba(0,0,0,0.03);
          color: #4b5563;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, border-color 0.15s;
          flex-shrink: 0;
          line-height: 1;
        }
        .bc-qty-btn:hover:not(:disabled) {
          background: rgba(0,0,0,0.08);
          border-color: rgba(0,0,0,0.20);
          color: #111;
        }
        .bc-qty-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .bc-qty-input {
          width: 52px;
          height: 28px;
          border: 1px solid rgba(0,0,0,0.12);
          border-radius: 6px;
          background: rgba(0,0,0,0.03);
          color: #111;
          font-size: 0.85rem;
          font-weight: 600;
          text-align: center;
          outline: none;
          padding: 0 4px;
          -moz-appearance: textfield;
        }
        .bc-qty-input::-webkit-inner-spin-button,
        .bc-qty-input::-webkit-outer-spin-button { -webkit-appearance: none; }
        .bc-qty-input:focus {
          border-color: rgba(139,92,246,0.6);
          box-shadow: 0 0 0 2px rgba(139,92,246,0.15);
        }
        .bc-qty-unit {
          font-size: 0.78rem;
          color: #9ca3af;
          font-weight: 500;
          padding: 0 2px;
          letter-spacing: 0.02em;
        }
      `}</style>
    </>
  );
}
